import type { ArcadeCutStats, ArcadeStats, ArcadeThrustStats } from './stats';
import type { Outcome } from './arcadeScoring';
import { TECHNIQUE, type Difficulty, type Technique, type Zone } from '../config/constants';

/** One resolved arcade target, logged for end-of-run aggregation. */
export type ArcadeShot = {
  tMs: number;
  outcome: Outcome;
  /** Points this resolution added (0 for a hard miss). */
  pointsGained: number;
  /** Combo streak after this shot resolved — the series' running max is the run's maxCombo. */
  comboAfter: number;
  /** Cut hit: edge-alignment angle (deg). Undefined for thrusts and for wrong / timeout. */
  edgeAngleDeg?: number;
  /** Thrust hit: distance from the disc center (m). Undefined for cuts and for non-hits. */
  accuracyM?: number;
  /** Thrust hit: spawn→hit reaction (ms). Undefined for cuts and for non-hits. */
  reactionMs?: number;
  /** Thrust: the spawn zone of the resolved target (present on every thrust shot). */
  zone?: Zone;
};

const emptyZones = (): Record<Zone, number> => ({
  head: 0,
  shoulder: 0,
  chest: 0,
  flank: 0,
  leg: 0,
});

/** Whether a resolution was a successful hit (vs. a wrong direction or a TTL timeout). */
const isHit = (o: Outcome): boolean => o === 'clean' || o === 'sloppy';

export function emptyArcadeStats(technique: Technique, difficulty: Difficulty): ArcadeStats {
  const common = {
    kind: 'arcade' as const,
    difficulty,
    score: 0,
    maxCombo: 0,
    hits: 0,
    total: 0,
    accuracyPct: 0,
  };
  if (technique === TECHNIQUE.thrust) {
    return {
      ...common,
      technique: 'thrust',
      avgAccuracyM: 0,
      avgReactionMs: 0,
      hitRateByZone: emptyZones(),
      spawnsByZone: emptyZones(),
    };
  }
  return { ...common, technique: 'cut', avgEdgeDeg: 0 };
}

/**
 * Summarize a run from its shot log. `score` is the sum of points (order-independent),
 * `maxCombo` the peak streak, `hits` the successful resolutions (clean + sloppy). The
 * technique selects which quality diagnostics are computed (edge angle vs. centering/zone).
 */
export function aggregateArcade(
  shots: readonly ArcadeShot[],
  technique: Technique,
  difficulty: Difficulty,
): ArcadeStats {
  return technique === TECHNIQUE.thrust
    ? aggregateThrust(shots, difficulty)
    : aggregateCut(shots, difficulty);
}

function aggregateCut(shots: readonly ArcadeShot[], difficulty: Difficulty): ArcadeCutStats {
  if (shots.length === 0) return emptyArcadeStats(TECHNIQUE.cut, difficulty) as ArcadeCutStats;

  let score = 0;
  let maxCombo = 0;
  let hits = 0;
  let edgeSum = 0;
  let edgeCount = 0;

  for (const s of shots) {
    score += s.pointsGained;
    maxCombo = Math.max(maxCombo, s.comboAfter);
    if (isHit(s.outcome)) {
      hits++;
      if (s.edgeAngleDeg != null && isFinite(s.edgeAngleDeg)) {
        edgeSum += s.edgeAngleDeg;
        edgeCount++;
      }
    }
  }

  return {
    kind: 'arcade',
    difficulty,
    technique: 'cut',
    score,
    maxCombo,
    hits,
    total: shots.length,
    accuracyPct: (hits / shots.length) * 100,
    avgEdgeDeg: edgeCount > 0 ? edgeSum / edgeCount : 0,
  };
}

function aggregateThrust(shots: readonly ArcadeShot[], difficulty: Difficulty): ArcadeThrustStats {
  if (shots.length === 0)
    return emptyArcadeStats(TECHNIQUE.thrust, difficulty) as ArcadeThrustStats;

  let score = 0;
  let maxCombo = 0;
  let hits = 0;
  let accuracySum = 0;
  let reactionSum = 0;
  const spawnsByZone = emptyZones();
  const hitsByZone = emptyZones();

  for (const s of shots) {
    score += s.pointsGained;
    maxCombo = Math.max(maxCombo, s.comboAfter);
    if (s.zone) spawnsByZone[s.zone]++;
    if (isHit(s.outcome)) {
      hits++;
      if (s.zone) hitsByZone[s.zone]++;
      accuracySum += s.accuracyM ?? 0;
      reactionSum += s.reactionMs ?? 0;
    }
  }

  const hitRateByZone = emptyZones();
  (Object.keys(spawnsByZone) as Zone[]).forEach((z) => {
    hitRateByZone[z] = spawnsByZone[z] > 0 ? (hitsByZone[z] / spawnsByZone[z]) * 100 : 0;
  });

  return {
    kind: 'arcade',
    difficulty,
    technique: 'thrust',
    score,
    maxCombo,
    hits,
    total: shots.length,
    accuracyPct: (hits / shots.length) * 100,
    avgAccuracyM: hits > 0 ? accuracySum / hits : 0,
    avgReactionMs: hits > 0 ? reactionSum / hits : 0,
    hitRateByZone,
    spawnsByZone,
  };
}
